const SNAPSHOT_KEY_PREFIX = 'forum_post_snapshot_';
const SNAPSHOT_INDEX_KEY = 'FORUM_POST_SNAPSHOT_INDEX';
const MAX_SNAPSHOT_COUNT = 20;
const MAX_SNAPSHOT_CHARS = 80000;
const NORMAL_CONTENT_CHARS = 12000;
const COMPACT_CONTENT_CHARS = 2000;
const NORMAL_MEDIA_COUNT = 9;
const COMPACT_MEDIA_COUNT = 4;

const canUseSessionStorage = () =>
  typeof window !== 'undefined' && typeof window.sessionStorage !== 'undefined';

const normalizeId = (value) => {
  if (value === undefined || value === null) return '';
  return String(value);
};

const getPostId = (post = {}, fallback) => {
  if (post.postsId !== undefined && post.postsId !== null) return normalizeId(post.postsId);
  if (post.postId !== undefined && post.postId !== null) return normalizeId(post.postId);
  return normalizeId(fallback);
};

const getSnapshotKey = (postId) => `${SNAPSHOT_KEY_PREFIX}${postId}`;

const safeSetItem = (key, value) => {
  if (!canUseSessionStorage()) return false;
  try {
    window.sessionStorage.setItem(key, value);
    return true;
  } catch (error) {
    return false;
  }
};

const safeRemoveItem = (key) => {
  if (!canUseSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    // ignore storage failures
  }
};

const readSnapshotIndex = () => {
  if (!canUseSessionStorage()) return [];
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(SNAPSHOT_INDEX_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((item) => item && item.postId && item.key) : [];
  } catch (error) {
    return [];
  }
};

const writeSnapshotIndex = (index) => {
  safeSetItem(SNAPSHOT_INDEX_KEY, JSON.stringify(index));
};

const uniqAndSortIndex = (index) => {
  const map = {};
  index.forEach((item) => {
    const postId = normalizeId(item.postId);
    if (!postId) return;
    const current = map[postId];
    if (!current || (item.updatedAt || 0) > (current.updatedAt || 0)) {
      map[postId] = {
        postId,
        key: item.key || getSnapshotKey(postId),
        updatedAt: Number(item.updatedAt) || 0,
      };
    }
  });
  return Object.keys(map)
    .map((postId) => map[postId])
    .sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));
};

const collectLegacySnapshotKeys = () => {
  if (!canUseSessionStorage()) return [];
  const keys = [];
  try {
    for (let index = 0; index < window.sessionStorage.length; index += 1) {
      const key = window.sessionStorage.key(index);
      if (key && key.indexOf(SNAPSHOT_KEY_PREFIX) === 0) keys.push(key);
    }
  } catch (error) {
    return [];
  }
  return keys;
};

export const pruneForumPostSnapshots = (keepPostId, maxCount = MAX_SNAPSHOT_COUNT) => {
  if (!canUseSessionStorage()) return;
  const keepId = normalizeId(keepPostId);
  const normalizedMax = Math.max(0, Number(maxCount) || 0);
  const indexed = uniqAndSortIndex(readSnapshotIndex());
  const indexedKeys = indexed.map((item) => item.key);
  const legacyKeys = collectLegacySnapshotKeys().filter((key) => indexedKeys.indexOf(key) === -1);
  const keepKey = keepId ? getSnapshotKey(keepId) : '';
  const kept = [];
  const removed = [];

  indexed.forEach((item) => {
    if (item.key === keepKey || kept.length < normalizedMax) kept.push(item);
    else removed.push(item.key);
  });

  legacyKeys.forEach((key) => {
    if (key === keepKey) return;
    if (kept.length < normalizedMax) {
      kept.push({ postId: key.replace(SNAPSHOT_KEY_PREFIX, ''), key, updatedAt: 0 });
    } else {
      removed.push(key);
    }
  });

  removed.forEach(safeRemoveItem);
  writeSnapshotIndex(uniqAndSortIndex(kept));
};

const pickMedia = (media = {}) => ({
  detailId: media.detailId,
  id: media.id,
  picUrl: media.picUrl,
  picBlurryUrl: media.picBlurryUrl,
  videoUrl: media.videoUrl,
  duration: media.duration,
  width: media.width,
  height: media.height,
});

const pickTag = (tag = {}) => ({
  tagName: tag.tagName,
  colour: tag.colour,
  backgroundImage: tag.backgroundImage,
});

const buildSnapshot = (post = {}, postId, mode) => {
  const compact = mode === 'compact';
  const minimal = mode === 'minimal';
  const contentLimit = minimal ? 0 : compact ? COMPACT_CONTENT_CHARS : NORMAL_CONTENT_CHARS;
  const mediaLimit = minimal ? 0 : compact ? COMPACT_MEDIA_COUNT : NORMAL_MEDIA_COUNT;
  const mediaList = post.postsDetailVos || post.detailList || [];
  const snapshot = {
    postsId: postId,
    postId: post.postId || postId,
    uid: post.uid,
    userId: post.userId,
    authorUid: post.authorUid,
    publisherUid: post.publisherUid,
    groupId: post.groupId,
    groupName: post.groupName,
    title: post.title,
    content: contentLimit ? String(post.content || '').slice(0, contentLimit) : '',
    avatar: post.avatar,
    frameUrl: post.frameUrl,
    nickname: post.nickname,
    timeDesc: post.timeDesc,
    createTime: post.createTime,
    createdAt: post.createdAt,
    detailType: post.detailType,
    postsType: post.postsType,
    essence: post.essence,
    topStatus: post.topStatus,
    topColor: post.topColor,
    praise: post.praise,
    collect: post.collect,
    praiseNum: post.praiseNum,
    collectNum: post.collectNum,
    commentNum: post.commentNum,
  };
  if (post.userLevelFrameInfoVo) {
    snapshot.userLevelFrameInfoVo = {
      frameUrl: post.userLevelFrameInfoVo.frameUrl,
      nameplate: post.userLevelFrameInfoVo.nameplate,
      level: post.userLevelFrameInfoVo.level,
    };
  }
  if (Array.isArray(post.postTagVos)) snapshot.postTagVos = post.postTagVos.slice(0, 8).map(pickTag);
  if (Array.isArray(mediaList) && mediaLimit) snapshot.postsDetailVos = mediaList.slice(0, mediaLimit).map(pickMedia);
  return snapshot;
};

const stringifySnapshot = (post, postId, mode) => {
  const snapshot = buildSnapshot(post, postId, mode);
  return JSON.stringify(snapshot);
};

export const saveForumPostSnapshot = (post = {}, fallbackPostId) => {
  const postId = getPostId(post, fallbackPostId);
  if (!postId || !canUseSessionStorage()) return false;
  const key = getSnapshotKey(postId);
  pruneForumPostSnapshots(postId, MAX_SNAPSHOT_COUNT - 1);

  let payload = stringifySnapshot(post, postId);
  if (payload.length > MAX_SNAPSHOT_CHARS) payload = stringifySnapshot(post, postId, 'compact');
  if (payload.length > MAX_SNAPSHOT_CHARS) payload = stringifySnapshot(post, postId, 'minimal');

  const trySave = () => safeSetItem(key, payload);
  if (!trySave()) {
    pruneForumPostSnapshots(postId, Math.floor(MAX_SNAPSHOT_COUNT / 2));
    if (!trySave()) {
      safeRemoveItem(key);
      pruneForumPostSnapshots(postId, 0);
      if (!trySave()) return false;
    }
  }

  const nextIndex = uniqAndSortIndex([
    { postId, key, updatedAt: Date.now() },
    ...readSnapshotIndex(),
  ]).slice(0, MAX_SNAPSHOT_COUNT);
  writeSnapshotIndex(nextIndex);
  return true;
};

export const readForumPostSnapshot = (postId) => {
  const id = normalizeId(postId);
  if (!id || !canUseSessionStorage()) return null;
  const key = getSnapshotKey(id);
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    safeRemoveItem(key);
    return null;
  }
};
